import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function WebsitePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activePerspective, setActivePerspective] = useState<'owner' | 'team' | 'visitor'>('owner');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const perspectives = {
    owner: {
      title: 'Business Owner',
      icon: 'ri-briefcase-line',
      benefits: [
        { icon: 'ri-user-add-line', text: 'Convert more visitors into qualified leads automatically' },
        { icon: 'ri-trophy-line', text: 'Establish instant credibility with professional design' },
        { icon: 'ri-line-chart-line', text: 'Increase revenue with optimized conversion paths' },
        { icon: 'ri-time-line', text: 'Stop wasting time on tech issues and updates' }
      ]
    },
    team: {
      title: 'Your Team',
      icon: 'ri-team-line',
      benefits: [
        { icon: 'ri-edit-box-line', text: 'Simple tools to update content without technical skills' },
        { icon: 'ri-customer-service-2-line', text: 'Better customer interactions with integrated systems' },
        { icon: 'ri-dashboard-line', text: 'Clear dashboard to track leads and inquiries' },
        { icon: 'ri-emotion-happy-line', text: 'Less frustration dealing with outdated technology' }
      ]
    },
    visitor: {
      title: 'Your Visitors',
      icon: 'ri-user-heart-line',
      benefits: [
        { icon: 'ri-flashlight-line', text: 'Fast-loading pages that respect their time' },
        { icon: 'ri-smartphone-line', text: 'Perfect experience on any device—phone, tablet, or desktop' },
        { icon: 'ri-shield-check-line', text: 'Clear, trustworthy design that feels professional' },
        { icon: 'ri-phone-line', text: 'Easy ways to contact you or book appointments instantly' }
      ]
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg' : 'bg-white shadow-md'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <img 
                src="https://static.readdy.ai/image/56592420ad0c806e82c83516867f5679/d1b89c1c9827b7fc761aa972a1497809.png" 
                alt="Smart Site Solutions" 
                className="h-10 w-auto"
              />
              <span className="text-xl font-bold text-[#1A2332]">Smart Site Solutions</span>
            </Link>
            
            <div className="hidden md:flex items-center space-x-8">
              <Link 
                to="/"
                className="text-base font-medium text-gray-700 hover:text-[#4A9FF5] transition-colors"
              >
                Home
              </Link>
              <button 
                onClick={() => scrollToSection('contact')}
                className="bg-[#4A9FF5] text-white px-6 py-2.5 rounded-full font-medium hover:bg-[#2B7FE8] transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                Get Started
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#4A9FF5] via-[#3DD5D5] to-[#4A9FF5] pt-20">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 right-10 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 left-10 w-80 h-80 bg-white/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
          <div className="inline-block px-4 py-2 rounded-full bg-white/20 text-white font-medium mb-6 backdrop-blur-sm">
            Conversion-Focused Website Design
          </div>
          
          <h1 className="text-6xl md:text-7xl font-bold text-white leading-tight mb-6 animate-fade-in-up">
            Your Business Needs a Website<br />
            <span className="text-white/90">
              That Captures Leads, Not Just Looks Good
            </span>
          </h1>

          <p className="text-xl text-white/90 max-w-2xl mx-auto mb-10 leading-relaxed animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            Whether you're starting from scratch or replacing an outdated site—get a custom-built website designed to convert visitors into paying customers while establishing instant credibility.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-5 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
            <button 
              onClick={() => scrollToSection('contact')}
              className="bg-white text-[#4A9FF5] px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-50 transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Get Your Custom Website
            </button>
            <button 
              onClick={() => scrollToSection('pain-points')}
              className="bg-transparent text-white px-8 py-4 rounded-full text-lg font-semibold border-2 border-white hover:bg-white hover:text-[#4A9FF5] transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              See What We Solve
            </button>
          </div>
        </div>

        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-3xl text-white"></i>
        </div>
      </section>

      {/* Pain Points Section */}
      <section id="pain-points" className="py-24 bg-[#F7F9FC]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 rounded-lg bg-red-500 text-white font-medium mb-4">
              The Hidden Cost of No Website—Or a Bad One
            </div>
            <h2 className="text-5xl font-bold text-[#1A2332] mb-4">
              Is Your Business Invisible Online?<br />Or Losing Customers Every Day?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Without a website—or with an outdated, slow, confusing one—you're actively losing business to competitors
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mb-6">
                <i className="ri-eye-off-line text-3xl text-red-500"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1A2332] mb-3">Invisible to New Customers</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Without a website, potential customers can't find you online. They search for your services, see your competitors, and never know you exist.
              </p>
              <div className="text-red-500 font-bold text-lg">Lost opportunities = lost revenue</div>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mb-6">
                <i className="ri-computer-line text-3xl text-red-500"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1A2332] mb-3">Outdated Design Kills Trust</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Visitors judge your business in 0.05 seconds. An old or unprofessional website makes them leave immediately—and choose your competitors instead.
              </p>
              <div className="text-red-500 font-bold text-lg">Lost credibility = lost revenue</div>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mb-6">
                <i className="ri-speed-line text-3xl text-red-500"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1A2332] mb-3">Slow Sites Lose Leads</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                53% of mobile visitors leave if a page takes longer than 3 seconds to load. Every second of delay costs you customers.
              </p>
              <div className="text-red-500 font-bold text-lg">Speed = conversions</div>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mb-6">
                <i className="ri-smartphone-line text-3xl text-red-500"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1A2332] mb-3">Mobile Disasters</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Over 60% of searches happen on mobile. If your site doesn't work perfectly on phones—or you don't have one—you're losing the majority of potential customers.
              </p>
              <div className="text-red-500 font-bold text-lg">Mobile-first or lose</div>
            </div>
          </div>
        </div>
      </section>

      {/* Perspectives Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-[#1A2332] mb-4">
              A Website That Works for Everyone
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See how professional website design improves your business from every angle
            </p>
          </div>

          {/* Perspective Tabs */}
          <div className="flex justify-center mb-12">
            <div className="inline-flex bg-[#F7F9FC] rounded-full p-2 gap-2">
              <button
                onClick={() => setActivePerspective('owner')}
                className={`px-8 py-3 rounded-full font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activePerspective === 'owner'
                    ? 'bg-[#4A9FF5] text-white shadow-lg'
                    : 'text-gray-600 hover:text-[#4A9FF5]'
                }`}
              >
                <i className="ri-briefcase-line mr-2"></i>
                Business Owner
              </button>
              <button
                onClick={() => setActivePerspective('team')}
                className={`px-8 py-3 rounded-full font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activePerspective === 'team'
                    ? 'bg-[#4A9FF5] text-white shadow-lg'
                    : 'text-gray-600 hover:text-[#4A9FF5]'
                }`}
              >
                <i className="ri-team-line mr-2"></i>
                Your Team
              </button>
              <button
                onClick={() => setActivePerspective('visitor')}
                className={`px-8 py-3 rounded-full font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activePerspective === 'visitor'
                    ? 'bg-[#4A9FF5] text-white shadow-lg'
                    : 'text-gray-600 hover:text-[#4A9FF5]'
                }`}
              >
                <i className="ri-user-heart-line mr-2"></i>
                Your Visitors
              </button>
            </div>
          </div>

          {/* Perspective Content */}
          <div className="bg-gradient-to-br from-[#F7F9FC] to-white rounded-3xl p-12 shadow-xl">
            <div className="flex items-center justify-center mb-8">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#4A9FF5] to-[#3DD5D5] flex items-center justify-center">
                <i className={`${perspectives[activePerspective].icon} text-4xl text-white`}></i>
              </div>
            </div>
            
            <h3 className="text-4xl font-bold text-[#1A2332] text-center mb-12">
              {perspectives[activePerspective].title} Perspective
            </h3>

            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {perspectives[activePerspective].benefits.map((benefit, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-full bg-[#4A9FF5]/10 flex items-center justify-center flex-shrink-0">
                      <i className={`${benefit.icon} text-2xl text-[#4A9FF5]`}></i>
                    </div>
                    <p className="text-gray-700 text-lg leading-relaxed pt-2">{benefit.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 bg-[#F7F9FC]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 rounded-lg bg-[#4A9FF5] text-white font-medium mb-4">
              Built for Service Businesses
            </div>
            <h2 className="text-5xl font-bold text-[#1A2332] mb-4">
              How We Build Websites That Convert
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Whether you're starting fresh or replacing an old site—every website is custom-designed to capture leads, save time, and grow your business
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="bg-white rounded-3xl p-10 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#4A9FF5] to-[#3DD5D5] flex items-center justify-center text-white text-2xl font-bold mb-6">
                <i className="ri-focus-3-line text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1A2332] mb-4">Conversion-Focused Design</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Every element is strategically placed to guide visitors toward contacting you or booking appointments—not just browsing and leaving.
              </p>
              <div className="space-y-2">
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <i className="ri-check-line text-[#4A9FF5]"></i>
                  <span>Clear calls-to-action everywhere</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <i className="ri-check-line text-[#4A9FF5]"></i>
                  <span>Trust-building design elements</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <i className="ri-check-line text-[#4A9FF5]"></i>
                  <span>Optimized lead capture forms</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-10 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#4A9FF5] to-[#3DD5D5] flex items-center justify-center text-white text-2xl font-bold mb-6">
                <i className="ri-smartphone-line text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1A2332] mb-4">Mobile-First &amp; Lightning Fast</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Built to load instantly on any device. Your site works perfectly whether customers find you on their phone, tablet, or desktop.
              </p>
              <div className="space-y-2">
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <i className="ri-check-line text-[#4A9FF5]"></i>
                  <span>Optimized for mobile searches</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <i className="ri-check-line text-[#4A9FF5]"></i>
                  <span>Sub-3-second load times</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <i className="ri-check-line text-[#4A9FF5]"></i>
                  <span>Responsive on all screen sizes</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-10 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#4A9FF5] to-[#3DD5D5] flex items-center justify-center text-white text-2xl font-bold mb-6">
                <i className="ri-settings-3-line text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1A2332] mb-4">Easy to Manage</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                No technical skills required. Update content, add services, or make changes yourself—or let us handle it for you.
              </p>
              <div className="space-y-2">
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <i className="ri-check-line text-[#4A9FF5]"></i>
                  <span>Simple content management</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <i className="ri-check-line text-[#4A9FF5]"></i>
                  <span>Automatic security updates</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <i className="ri-check-line text-[#4A9FF5]"></i>
                  <span>Ongoing support included</span>
                </div>
              </div>
            </div>
          </div>

          {/* Build Process */}
          <div className="bg-white rounded-3xl p-12 shadow-xl">
            <h3 className="text-3xl font-bold text-[#1A2332] text-center mb-12">
              From Concept to Launch in 2-4 Weeks
            </h3>
            
            <div className="grid md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-[#4A9FF5] text-white text-2xl font-bold flex items-center justify-center mx-auto mb-4">
                  1
                </div>
                <h4 className="font-bold text-[#1A2332] mb-2">Discovery &amp; Strategy</h4>
                <p className="text-sm text-gray-600">We learn your business, audience, and goals</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-[#4A9FF5] text-white text-2xl font-bold flex items-center justify-center mx-auto mb-4">
                  2
                </div>
                <h4 className="font-bold text-[#1A2332] mb-2">Design &amp; Build</h4>
                <p className="text-sm text-gray-600">Custom design optimized for your industry</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-[#4A9FF5] text-white text-2xl font-bold flex items-center justify-center mx-auto mb-4">
                  3
                </div>
                <h4 className="font-bold text-[#1A2332] mb-2">Review &amp; Refine</h4>
                <p className="text-sm text-gray-600">You review and we perfect every detail</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-[#4A9FF5] text-white text-2xl font-bold flex items-center justify-center mx-auto mb-4">
                  4
                </div>
                <h4 className="font-bold text-[#1A2332] mb-2">Launch &amp; Grow</h4>
                <p className="text-sm text-gray-600">Go live and start capturing leads</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Outcomes Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 rounded-lg bg-[#4A9FF5]/10 text-[#4A9FF5] font-medium mb-4">
              Real Business Results
            </div>
            <h2 className="text-5xl font-bold text-[#1A2332] mb-4">
              What You Get With a Professional Website
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-gradient-to-br from-[#4A9FF5] to-[#3DD5D5] rounded-3xl p-10 text-white shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
              <div className="text-6xl font-bold mb-4">3x</div>
              <h3 className="text-2xl font-bold mb-3">More Leads</h3>
              <p className="text-white/90 leading-relaxed">
                Conversion-optimized design turns more visitors into qualified leads—without spending more on advertising.
              </p>
            </div>

            <div className="bg-gradient-to-br from-[#4A9FF5] to-[#3DD5D5] rounded-3xl p-10 text-white shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
              <div className="text-6xl font-bold mb-4">&lt;3s</div>
              <h3 className="text-2xl font-bold mb-3">Load Time</h3>
              <p className="text-white/90 leading-relaxed">
                Lightning-fast performance keeps visitors engaged and prevents them from leaving before they even see your services.
              </p>
            </div>

            <div className="bg-gradient-to-br from-[#4A9FF5] to-[#3DD5D5] rounded-3xl p-10 text-white shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
              <div className="text-6xl font-bold mb-4">24/7</div>
              <h3 className="text-2xl font-bold mb-3">Always Working</h3>
              <p className="text-white/90 leading-relaxed">
                Your website captures leads and builds trust around the clock—even when you're closed or busy with other work.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-[#F7F9FC] rounded-3xl p-10 shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-14 h-14 rounded-full bg-[#4A9FF5]/10 flex items-center justify-center">
                  <i className="ri-trophy-line text-3xl text-[#4A9FF5]"></i>
                </div>
                <h3 className="text-2xl font-bold text-[#1A2332]">Instant Credibility</h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Professional design establishes trust immediately. Visitors see you as the established, reliable choice—not the risky option or invisible competitor.
              </p>
            </div>

            <div className="bg-[#F7F9FC] rounded-3xl p-10 shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-14 h-14 rounded-full bg-[#4A9FF5]/10 flex items-center justify-center">
                  <i className="ri-time-line text-3xl text-[#4A9FF5]"></i>
                </div>
                <h3 className="text-2xl font-bold text-[#1A2332]">Save Hours Every Week</h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Stop dealing with tech issues, broken plugins, and confusing updates. Focus on growing your business instead of managing technology.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="py-24 bg-gradient-to-br from-[#1A2332] via-[#2B3F5C] to-[#1A2332] relative overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-[#4A9FF5]/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#3DD5D5]/20 rounded-full blur-3xl"></div>
        
        <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Ready for a Website That Converts?
          </h2>
          <p className="text-xl text-gray-300 mb-12">
            See exactly how a custom website will work for your business—free consultation, no pressure, fast response
          </p>

          <div className="bg-white rounded-3xl p-10 shadow-2xl">
            <form 
              id="website_contact_form"
              data-readdy-form
              action="https://readdy.ai/api/form/d58skd93kamldd15634g"
              method="POST"
              onSubmit={(e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const formData = new FormData(form);
                
                fetch('https://readdy.ai/api/form/d58skd93kamldd15634g', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                  },
                  body: new URLSearchParams(formData as any).toString(),
                })
                .then(response => {
                  if (response.ok) {
                    alert('Thank you! We\'ll be in touch within 24 hours to discuss your website needs.');
                    form.reset();
                  } else {
                    alert('Something went wrong. Please try again.');
                  }
                })
                .catch(() => {
                  alert('Something went wrong. Please try again.');
                });
              }}
              className="space-y-5"
            >
              <input
                type="text"
                name="name"
                placeholder="Your Name"
                required
                className="w-full px-6 py-4 rounded-xl border-2 border-gray-200 focus:border-[#4A9FF5] focus:outline-none text-base transition-colors"
              />
              <input
                type="email"
                name="email"
                placeholder="Your Email"
                required
                className="w-full px-6 py-4 rounded-xl border-2 border-gray-200 focus:border-[#4A9FF5] focus:outline-none text-base transition-colors"
              />
              <input
                type="tel"
                name="phone"
                placeholder="Your Phone Number"
                required
                className="w-full px-6 py-4 rounded-xl border-2 border-gray-200 focus:border-[#4A9FF5] focus:outline-none text-base transition-colors"
              />
              <input
                type="text"
                name="business_name"
                placeholder="Business Name"
                required
                className="w-full px-6 py-4 rounded-xl border-2 border-gray-200 focus:border-[#4A9FF5] focus:outline-none text-base transition-colors"
              />
              <input
                type="text"
                name="business_type"
                placeholder="Type of Business (e.g., HVAC, Plumbing, Consulting)"
                className="w-full px-6 py-4 rounded-xl border-2 border-gray-200 focus:border-[#4A9FF5] focus:outline-none text-base transition-colors"
              />
              <button
                type="submit"
                className="w-full bg-[#4A9FF5] text-white py-4 rounded-xl text-lg font-semibold hover:bg-[#2B7FE8] transition-all duration-300 hover:scale-[1.02] shadow-lg whitespace-nowrap cursor-pointer"
              >
                Get Your Free Website Consultation
              </button>
            </form>

            <div className="flex items-center justify-center space-x-6 mt-8 text-sm text-gray-600">
              <div className="flex items-center space-x-2">
                <i className="ri-check-line text-[#4A9FF5]"></i>
                <span>Response within 24 hours</span>
              </div>
              <div className="flex items-center space-x-2">
                <i className="ri-check-line text-[#4A9FF5]"></i>
                <span>No obligation</span>
              </div>
              <div className="flex items-center space-x-2">
                <i className="ri-check-line text-[#4A9FF5]"></i>
                <span>Custom design</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1A2332] py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <Link to="/" className="flex items-center space-x-3 mb-4">
                <img 
                  src="https://static.readdy.ai/image/56592420ad0c806e82c83516867f5679/d1b89c1c9827b7fc761aa972a1497809.png" 
                  alt="Smart Site Solutions" 
                  className="h-8 w-auto"
                />
                <span className="text-xl font-bold text-white">Smart Site Solutions</span>
              </Link>
              <p className="text-gray-400">Websites and automation built for service businesses</p>
            </div>

            <div>
              <h4 className="text-base font-semibold text-white mb-4">Contact Us</h4>
              <div className="space-y-3">
                <div className="flex items-center space-x-2 text-gray-400">
                  <i className="ri-phone-line"></i>
                  <span>613-899-4468</span>
                </div>
                <div className="flex items-center space-x-2 text-gray-400">
                  <i className="ri-phone-line"></i>
                  <span>613-621-2422</span>
                </div>
                <div className="flex items-center space-x-2 text-gray-400">
                  <i className="ri-phone-line"></i>
                  <span>613-203-2762</span>
                </div>
                <div className="flex items-center space-x-2 text-gray-400">
                  <i className="ri-mail-line"></i>
                  <span>Smartsitesolutions1@gmail.com</span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-base font-semibold text-white mb-4">Quick Links</h4>
              <div className="space-y-3">
                <Link 
                  to="/"
                  className="block text-gray-400 hover:text-[#4A9FF5] transition-colors"
                >
                  Home
                </Link>
                <button 
                  onClick={() => scrollToSection('pain-points')}
                  className="block text-gray-400 hover:text-[#4A9FF5] transition-colors cursor-pointer text-left"
                >
                  What We Solve
                </button>
                <button 
                  onClick={() => scrollToSection('contact')}
                  className="block text-gray-400 hover:text-[#4A9FF5] transition-colors cursor-pointer text-left"
                >
                  Contact
                </button>
              </div>
            </div>

            <div>
              <h4 className="text-base font-semibold text-white mb-4">Get Started</h4>
              <button 
                onClick={() => scrollToSection('contact')}
                className="bg-[#4A9FF5] text-white px-6 py-3 rounded-full font-medium hover:bg-[#2B7FE8] transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Schedule Consultation
              </button>
            </div>
          </div>

          <div className="pt-8 border-t-2 border-[#4A9FF5]/20 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-400 text-sm">© 2025 Smart Site Solutions. All rights reserved.</p>
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-400 text-sm hover:text-[#4A9FF5] transition-colors cursor-pointer"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
